import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';

export type PixelMode = 'mandatory' | 'optional' | 'hidden';

const VALID_MODES: PixelMode[] = ['mandatory', 'optional', 'hidden'];

function isPixelMode(value: unknown): value is PixelMode {
    return typeof value === 'string' && VALID_MODES.includes(value as PixelMode);
}

/**
 * The Pixel install mode that actually applies to a given seller: their per-account
 * override (set by an admin in the back office) wins over the platform-wide mode.
 * Mirrors the backend resolution in leadrs-back/src/services/pixelConfig.ts + the
 * pixelModeOverride check in adminController's PATCH /admin/users/:id.
 */
export function resolveEffectivePixelMode(globalMode: PixelMode | null | undefined, user: any): PixelMode {
    if (isPixelMode(user?.pixelModeOverride)) return user.pixelModeOverride;
    return isPixelMode(globalMode) ? globalMode : 'mandatory';
}

/**
 * Fetches the platform-wide Pixel mode once (public endpoint, same one the
 * seller-facing integration UI already polls). Combine with resolveEffectivePixelMode
 * and the current user to get the mode that applies to them.
 */
export function usePixelMode() {
    const [globalMode, setGlobalMode] = useState<PixelMode | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${CONFIG.apiUrl}/leadrs-pixel/config`)
            .then((res) => {
                if (!cancelled) setGlobalMode(isPixelMode(res.data?.mode) ? res.data.mode : 'mandatory');
            })
            .catch(() => {
                if (!cancelled) setGlobalMode('mandatory');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return { globalMode, loading };
}
